import React, { useState, useEffect } from "react";
import {
  Select,
  DatePicker,
  Space,
  Collapse,
  Input,
  Image,
  Checkbox,
  Tag,
  Typography,
  Form,
} from "antd";
import {
  WarningOutlined,
  ExclamationCircleOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import { CATEGORY_COLORS } from "./eventCategories";

const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

// Causas que además requieren precisar quién estuvo involucrado.
export const CAUSAS_CON_DETALLE = [
  "lesiones-personales",
  "injurias-calumnias",
  "injuria-vias-hecho",
];

export const CATEGORY_STYLE = {
  riesgo: { color: CATEGORY_COLORS.riesgo, iconColor: "#d4a017", icon: WarningOutlined },
  contravención: {
    color: CATEGORY_COLORS.contravención,
    iconColor: "#1890ff",
    icon: ExclamationCircleOutlined,
  },
  delito: { color: CATEGORY_COLORS.delito, iconColor: "#ff4d4f", icon: AlertOutlined },
};

export const LocationSelector = ({
  form,
  locations,
  selectedLocation,
  setSelectedLocation,
  zonaPreSeleccionada = null,
  initialLocation = null,
}) => {
  const handleLocationChange = (value) => {
    const location = JSON.parse(value);
    setSelectedLocation(location);
  };

  const [zonaSeleccionada, setZonaSeleccionada] = useState(zonaPreSeleccionada);
  const [medioSeleccionado, setMedioSeleccionado] = useState(null);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [nombreSeleccionado, setNombreSeleccionado] = useState(null);
  const [ubicacionEspecifica, setUbicacionEspecifica] = useState(null);

  // Efecto para actualizar la zona seleccionada cuando cambie la zona pre-seleccionada
  useEffect(() => {
    if (zonaPreSeleccionada) {
      setZonaSeleccionada(zonaPreSeleccionada);
      // Limpiar las otras selecciones cuando cambie la zona
      setMedioSeleccionado(null);
      setTipoSeleccionado(null);
      setNombreSeleccionado(null);
      setUbicacionEspecifica(null);
    }
  }, [zonaPreSeleccionada]);

  // Precarga la cascada de selects con la ubicación actual del evento (modo edición).
  useEffect(() => {
    if (!initialLocation || !locations?.length) return;

    const match = locations.find(
      (loc) =>
        loc.zone === initialLocation.zone &&
        loc.means === initialLocation.means &&
        loc.type === initialLocation.type &&
        loc.name === initialLocation.name
    );

    setZonaSeleccionada(initialLocation.zone);
    setMedioSeleccionado(initialLocation.means);
    setTipoSeleccionado(initialLocation.type);

    if (match) {
      setNombreSeleccionado(JSON.stringify(match));
      setSelectedLocation(match);
      form.setFieldValue("Lugar", JSON.stringify(match));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation, locations]);

  const zonas = [...new Set(locations.map((item) => item.zone))].sort(
    (a, b) => a - b
  );

  const medios = [
    ...new Set(
      locations
        .filter((item) => item.zone === zonaSeleccionada)
        .map((item) => item.means)
    ),
  ];

  const tipos = [
    ...new Set(
      locations
        .filter(
          (item) =>
            item.zone === zonaSeleccionada && item.means === medioSeleccionado
        )
        .map((item) => item.type)
    ),
  ];

  const nombres = locations.filter(
    (item) =>
      item.zone === zonaSeleccionada &&
      item.means === medioSeleccionado &&
      item.type === tipoSeleccionado
  );

  const ubicacionesEspecificas = {
    ESTACION: [
      "P5",
      "Ascensor A",
      "Ascensor B",
      "Cuartos técnicos",
      "Escaleras de ingreso",
      "Escaleras internas",
      "Garaje",
      "Local Comercial",
      "Mezanine zona paga",
      "Mezanine zona no paga",
      "Pasarela o puente peatonal",
      "Plataforma A",
      "Plataforma B",
      "Plazoleta (Ubicación dentro de plazoleta: Ascensor A, Ascensor B, Local comercial Alcaldia, Local comercial Meto, Nor-occidente, nor-oriente, norte, occidente, oriente, sur, sur-occidente, sur-oriente, zona verde)",
      "Punto cero",
      "Punto de recarga",
      "Punto de venta",
      "Techos",
      "Torniquetes",
      "Via A",
      "Via B",
      "Zona de máquinas vending",
      "Ingreso OCCIDENTE",
      "Ingreso ORIENTE",
      "Ingreso NORTE",
      "Ingreso SUR",
    ],
    "INTER-ESTACION": [
      "Bajos del viaducto",
      "Columna",
      "Local comercial Alcaldia",
      "Local comercial Metro",
      "Material rodante",
      "Pilona",
      "Via a nivel",
      "Via A",
      "Via B",
      "Ingreso OCCIDENTE",
      "Ingreso ORIENTE",
      "Ingreso NORTE",
      "Ingreso SUR",
    ],
    PARADA: [
      "C1",
      "C2",
      "C3",
      "Cuarto técnicos",
      "Mezanine zona no paga",
      "Mezanine zona paga",
      "Nichos",
      "P5",
      "Plataforma A",
      "Plataforma B",
      "Puerta de abordaje",
      "Punto de venta",
      "Salidas de emergencia",
    ],
    PARADERO: [], // vacío o lo puedes llenar si tienes opciones
  };

  const opcionesUbicacionEspecifica =
    ubicacionesEspecificas[tipoSeleccionado] || [];

  return (
    <>
      <Form.Item label="Fecha y Hora" name="date">
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          style={{ width: "100%" }}
        />
      </Form.Item>

      <Form.Item
        label="Zona"
        rules={[{ required: true, message: "Debes completar este campo." }]}
      >
        <Select
          placeholder="Selecciona la zona"
          onChange={(value) => {
            setZonaSeleccionada(value);
            setMedioSeleccionado(null);
            setTipoSeleccionado(null);
            setNombreSeleccionado(null);
            setUbicacionEspecifica(null);
          }}
          value={zonaSeleccionada}
        >
          {zonas.map((z) => (
            <Option key={z} value={z}>
              Zona {z}
            </Option>
          ))}
        </Select>
      </Form.Item>

      {zonaSeleccionada && (
        <Form.Item
          label="Medio"
          rules={[{ required: true, message: "Debes completar este campo." }]}
        >
          <Select
            placeholder="Selecciona el medio"
            onChange={(value) => {
              setMedioSeleccionado(value);
              setTipoSeleccionado(null);
              setNombreSeleccionado(null);
              setUbicacionEspecifica(null);
            }}
            value={medioSeleccionado}
          >
            {medios.map((m) => (
              <Option key={m} value={m}>
                {m}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {medioSeleccionado && (
        <Form.Item
          label="Tipo"
          rules={[{ required: true, message: "Debes completar este campo." }]}
        >
          <Select
            placeholder="Selecciona el tipo"
            onChange={(value) => {
              setTipoSeleccionado(value);
              setNombreSeleccionado(null);
              setUbicacionEspecifica(null);
            }}
            value={tipoSeleccionado}
          >
            {tipos.map((t) => (
              <Option key={t} value={t}>
                {t}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {tipoSeleccionado && (
        <Form.Item
          label="Nombre Ubicación"
          name="Lugar"
          rules={[{ required: true, message: "Debes completar este campo." }]}
        >
          <Select
            placeholder="Ubicación del evento"
            style={{ width: "100%" }}
            showSearch
            onChange={(value) => {
              handleLocationChange(value);
              setNombreSeleccionado(value);
              setUbicacionEspecifica(null);
            }}
            value={nombreSeleccionado}
          >
            {nombres.map((item) => (
              <Option key={item.name} value={JSON.stringify(item)}>
                {item.specific} - {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {tipoSeleccionado && opcionesUbicacionEspecifica.length > 0 && (
        <Form.Item label="Ubicación Específica" name="ubicacionEspecifica">
          <Select
            placeholder="Selecciona la ubicación específica"
            onChange={setUbicacionEspecifica}
            value={ubicacionEspecifica}
            showSearch
          >
            {opcionesUbicacionEspecifica.map((u) => (
              <Select.Option key={u} value={u}>
                {u}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </>
  );
};

export const EventTypeSelector = ({ categories, selectedItems, setSelectedItems }) => {
  const isChecked = (itemKey) =>
    selectedItems.some((i) => i.key === itemKey);

  // La regla de negocio exige que todas las causas seleccionadas compartan
  // categoría y riesgo (ver validación en handleSubmit), así que bloqueamos
  // de forma proactiva cualquier opción que rompería esa combinación.
  const activeCategory = selectedItems[0]?.category ?? null;
  const activeSubItem = selectedItems[0]?.subItem ?? null;

  const toggleItem = (item) => {
    setSelectedItems((prev) =>
      prev.some((i) => i.key === item.key)
        ? prev.filter((i) => i.key !== item.key)
        : [...prev, item]
    );
  };

  const countInCategory = (categoryKey) =>
    selectedItems.filter((i) => i.category === categoryKey).length;

  const countInSubItem = (categoryKey, subItemKey) =>
    selectedItems.filter(
      (i) => i.category === categoryKey && i.subItem === subItemKey
    ).length;

  const isOptionDisabled = (categoryKey, subItemKey, itemKey) => {
    if (isChecked(itemKey)) return false;
    if (!activeCategory) return false;
    return activeCategory !== categoryKey || activeSubItem !== subItemKey;
  };

  const renderOption = (categoryKey, subItemKey, option) => {
    const itemKey = `${categoryKey}:${subItemKey}:${option.value}`;
    return (
      <Checkbox
        key={itemKey}
        checked={isChecked(itemKey)}
        disabled={isOptionDisabled(categoryKey, subItemKey, itemKey)}
        onChange={() =>
          toggleItem({
            key: itemKey,
            category: categoryKey,
            subItem: subItemKey,
            value: option.value,
            label: option.label,
            protocol: option.protocol,
          })
        }
      >
        {option.label}
      </Checkbox>
    );
  };

  // Sub-colapsables de una categoría con submenú (Riesgos de Proceso, Otros).
  // Extraído para poder reutilizarlo tanto envuelto en el Panel de la
  // categoría como, cuando category.hideLabel lo pide, directo sin ese
  // envoltorio.
  const renderNestedSubItems = (category, style) => (
    <Collapse bordered={false} ghost>
      {category.subItems.map((subItem) => {
        const subCount = countInSubItem(category.key, subItem.key);
        return (
          <Panel
            key={subItem.key}
            header={
              <Space>
                {subItem.image && (
                  <Image
                    src={subItem.image}
                    width={24}
                    height={24}
                    preview={false}
                    style={{
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                )}
                <span>{subItem.label}</span>
                {subCount > 0 && (
                  <Tag color={style?.color || "default"}>{subCount}</Tag>
                )}
              </Space>
            }
            style={{
              borderBottom: "1px solid #ddd",
              marginBottom: "8px",
            }}
          >
            <Space direction="vertical">
              {subItem.options.map((option) =>
                renderOption(category.key, subItem.key, option)
              )}
            </Space>
          </Panel>
        );
      })}
    </Collapse>
  );

  return (
    <>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Selecciona una o varias causas del mismo tipo y riesgo.
      </Text>
      {categories.map((category) => {
        const style = CATEGORY_STYLE[category.key];
        const CategoryIcon = style?.icon;
        const categoryCount = countInCategory(category.key);
        const hasNestedOptions = category.subItems?.[0]?.options !== undefined;

        // Algunos equipos (p. ej. EMER) solo ven un puñado de sub-menús de
        // "Otros"; agruparlos bajo el rótulo de la categoría no aporta nada
        // ahí, así que category.hideLabel salta directo a los sub-menús sin
        // el Panel envolvente.
        if (category.hideLabel) {
          return (
            <div key={category.key} style={{ marginBottom: 8 }}>
              {renderNestedSubItems(category, style)}
            </div>
          );
        }

        return (
          <Collapse bordered={false} key={category.key} style={{ marginBottom: 8 }}>
            <Panel
              key={category.key}
              header={
                <Space>
                  {CategoryIcon && (
                    <CategoryIcon style={{ color: style.iconColor }} />
                  )}
                  <span>{category.label}</span>
                  {categoryCount > 0 && (
                    <Tag color={style?.color || "default"}>{categoryCount}</Tag>
                  )}
                </Space>
              }
            >
              {hasNestedOptions ? (
                renderNestedSubItems(category, style)
              ) : (
                <Space direction="vertical">
                  {category.subItems.map((item) =>
                    renderOption(category.key, "na", item)
                  )}
                </Space>
              )}
            </Panel>
          </Collapse>
        );
      })}
    </>
  );
};

// Solo se puede fijar al editar el evento; al crearlo se envía vacía por defecto.
export const INTENTION_OPTIONS = [
  "Neutral",
  "Positivo",
  "Preventivo",
  "Correctivo",
  "Negativo",
];

export const EventDetails = ({ selectedItems, showIntention = false }) => {
  const requiereDetalle = selectedItems.some((it) =>
    CAUSAS_CON_DETALLE.includes(it.value)
  );
  const sourceSeleccionada = Form.useWatch("source");

  return (
    <>
      <>
        {requiereDetalle && (
          <Form.Item
            label="Detalle"
            name="titleDetail"
            rules={[{ required: true, message: "Debes completar este campo." }]}
          >
            <Select placeholder="Selecciona una opción">
              <Option value="Usuarios-vs-Funcionarios-Metro">
                Usuarios vs Funcionarios Metro
              </Option>
              <Option value="Usuarios-vs-Proveedor">
                Usuarios vs Proveedor
              </Option>
              <Option value="Usuario-y-Usuario">Usuario y Usuario</Option>
              <Option value="Usuario-y-aliado-PONAL">
                Usuario y aliado PONAL
              </Option>
            </Select>
          </Form.Item>
        )}
      </>

      <Form.Item
        label="Asunto de evento"
        name="title"
        rules={[{ required: true, message: "Debes de completar este campo." }]}
      >
        <TextArea placeholder={"¿QUE? ¿QUIEN? , ¿COMO?"} />
      </Form.Item>
      <Form.Item label="Fuente de información (Opcional)" name="source">
        <Select>
          <Select.Option value="Plataforma Cise">Plataforma Cise</Select.Option>
          <Select.Option value="Policía">Policía</Select.Option>
          <Select.Option value="Vigilancia privada">
            Vigilancia privada
          </Select.Option>
          <Select.Option value="Operador de medios tecnológicos">
            Operador de medios tecnológicos
          </Select.Option>
          <Select.Option value="Informe IHR">Informe IHR</Select.Option>
          <Select.Option value="Usuario Metro">Usuario Metro</Select.Option>
          <Select.Option value="Pasajero">Pasajero</Select.Option>
          <Select.Option value="Otro">Otro</Select.Option>
        </Select>
      </Form.Item>

      {sourceSeleccionada === "Otro" && (
        <Form.Item
          label="¿Cuál es la fuente de información?"
          name="sourceOtro"
          rules={[{ required: true, message: "Debes completar este campo." }]}
        >
          <Input placeholder="Escribe la fuente de información" />
        </Form.Item>
      )}

      {showIntention && (
        <Form.Item label="Intención" name="intention">
          <Select placeholder="Selecciona una opción" allowClear>
            {INTENTION_OPTIONS.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </>
  );
};
